import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';
import {
  parseRewriteRules,
  RewriteOperator,
  stringifyRewriteValue,
  type RewriteList,
  type RewriteListMutationPayload,
  type RewriteRule,
} from './model';

const required = () =>
  z
    .string()
    .trim()
    .min(1, { error: () => dashboardT('lists.validation.required') });

const conditionSchema = z.object({
  path: required(),
  op: z.enum(RewriteOperator),
  val: z.string(),
  val2: z.string(),
});

const setSchema = z.object({
  field: required(),
  value: z.string(),
  overwrite: z.boolean(),
  nullify: z.boolean(),
});

export const rewriteFormSchema = z.object({
  name: required(),
  rules: z
    .array(
      z.object({
        condGroups: z.array(z.object({ conditions: z.array(conditionSchema).min(1) })),
        sets: z.array(setSchema).min(1),
      }),
    )
    .min(1, { error: () => dashboardT('lists.rewrite.ruleRequired') }),
});

export type RewriteFormValue = z.infer<typeof rewriteFormSchema>;

export const defaultRewriteFormValue: RewriteFormValue = {
  name: '',
  rules: [emptyRule()],
};

export function rewriteListToFormValue(item: RewriteList) {
  const parsed = parseRewriteRules(item.rules);
  if (!parsed.success) return { success: false as const, error: parsed.error };
  return {
    success: true as const,
    value: {
      name: item.name,
      rules: parsed.data.length
        ? parsed.data.map((rule) => ({
            condGroups: rule.condGroups.map((conditions) => ({
              conditions: conditions.map((condition) => ({
                path: condition.path,
                op: condition.op,
                val: stringifyRewriteValue(condition.val),
                val2: stringifyRewriteValue(condition.val2),
              })),
            })),
            sets: rule.sets.length
              ? rule.sets.map((set) => ({
                  field: set.field,
                  value: stringifyRewriteValue(set.value),
                  overwrite: set.overwrite,
                  nullify: set.nullify,
                }))
              : [emptySet()],
          }))
        : defaultRewriteFormValue.rules,
    } satisfies RewriteFormValue,
  };
}

export function serializeRewriteFormValue(value: RewriteFormValue): RewriteListMutationPayload {
  const rules: RewriteRule[] = value.rules.map((rule) => ({
    condGroups: rule.condGroups.map((group) =>
      group.conditions.map((condition) => ({
        path: condition.path.trim(),
        op: condition.op,
        ...(needsRewriteValue(condition.op)
          ? { val: stringifyRewriteValue(condition.val).trim() }
          : {}),
        ...(condition.op === RewriteOperator.Between
          ? { val2: stringifyRewriteValue(condition.val2).trim() }
          : {}),
      })),
    ),
    sets: rule.sets.map((set) => ({
      field: set.field.trim(),
      value: stringifyRewriteValue(set.value).trim(),
      overwrite: set.value.trim() ? set.overwrite : false,
      nullify: set.value.trim() ? false : set.nullify,
    })),
  }));
  return { name: value.name.trim(), rules: JSON.stringify(rules) };
}

export function emptyCondition() {
  return { path: '', op: RewriteOperator.Equals, val: '', val2: '' } as const;
}

export function emptySet() {
  return { field: '', value: '', overwrite: false, nullify: false };
}

export function emptyRule() {
  return { condGroups: [], sets: [emptySet()] };
}

export function needsRewriteValue(operator: RewriteOperator) {
  return operator !== RewriteOperator.Exists && operator !== RewriteOperator.NotExists;
}
